import { useCallback, useState } from 'react'

/**
 * Sending a message on Mythzord.
 *
 * There is no delivery path. `verdict` is always a mute, and the hook models
 * that honestly rather than pretending a 200 might mean delivery — a
 * `delivered` boolean that is never true would just invite someone to branch
 * on it later.
 */
export interface SendState {
  pending: boolean
  verdict: string | null
  /** Set when Sergeant Chill answered from its fallback rather than the model. */
  degraded: boolean
  error: string | null
}

export function useSend() {
  const [state, setState] = useState<SendState>({
    pending: false,
    verdict: null,
    degraded: false,
    error: null,
  })

  const send = useCallback(async (channel: string, body: string) => {
    if (!body.trim()) return
    setState({ pending: true, verdict: null, degraded: false, error: null })
    try {
      const res = await fetch('/api/send', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ channel, body }),
      })
      const data = (await res.json().catch(() => null)) as
        | { verdict?: string; degraded?: boolean; error?: string }
        | null

      if (!res.ok || !data?.verdict) {
        // Never surface a raw failure as silence — that was the obsidia lesson.
        setState({
          pending: false,
          verdict: null,
          degraded: false,
          error: 'Sergeant Chill is reviewing an unusually large volume. Try again shortly.',
        })
        return
      }

      setState({ pending: false, verdict: data.verdict, degraded: !!data.degraded, error: null })
    } catch {
      setState({
        pending: false,
        verdict: null,
        degraded: false,
        error: 'Sergeant Chill could not be reached.',
      })
    }
  }, [])

  const dismiss = useCallback(
    () => setState({ pending: false, verdict: null, degraded: false, error: null }),
    [],
  )

  return { ...state, send, dismiss }
}
